import { setViewModel } from './supportSlice';
import { AnyAction } from '@reduxjs/toolkit';
import { Dispatch } from 'react';
import i18n from '../../i18n/config';

import { NavigateParams } from '../../components/BtNavigate';
import { ButtonParams } from '../../components/BtButton';
import { LoadingParams } from '../../components/BtLoading';

export function none(logoUrl: string): SupportScreen.None {
  return {
    type: 'none',
    texts: {
      title: '',
      body: '',
      phone: '',
      metaTitle: '',
    },
    loading: {
      title: '',
      navigation: {
        back: { title: '', image: 'ico_back_arrow', visible: false },
        home: { title: '', image: 'ico_home', visible: false },
        image: logoUrl,
      },
    },
  };
}

export namespace SupportScreen {
  interface Texts {
    title: string;
    body: string;
    phone: string;
    metaTitle: string;
  }

  export interface None {
    type: 'none';
    texts: Texts;
    loading: LoadingParams;
  }

  export interface Loading {
    type: 'loading';
    texts: Texts;
  }

  export interface Ready {
    type: 'ready';
    texts: Texts;
    navigation: NavigateParams;
    title: string;
    body: string;
    phone: string;
    close: ButtonParams;
    metaTitle: string;
    ikon: string;
  }

  export type ViewModel = None | Loading | Ready;

  export function isNone(viewModel: ViewModel): viewModel is None {
    return (viewModel as None).type === 'none';
  }
  export function isLoading(viewModel: ViewModel): viewModel is Loading {
    return (viewModel as Loading).type === 'loading';
  }
  export function isReady(viewModel: ViewModel): viewModel is Ready {
    return (viewModel as Ready).type === 'ready';
  }

  function getTexts(viewModel: ViewModel): Texts {
    if (isNone(viewModel)) return viewModel.texts;
    if (isLoading(viewModel)) return viewModel.texts;
    if (isReady(viewModel)) return viewModel.texts;
    return { title: '', body: '', phone: '', metaTitle: '' };
  }

  function loading(viewModel: ViewModel): Loading {
    const t = i18n.t;
    return {
      type: 'loading',
      texts: {
        title: t('supportScreen.title'),
        body: t('supportScreen.body'),
        phone: t('supportScreen.phone'),
        metaTitle: t('supportScreen.metaTitle'),
      },
    };
  }

  function ready(viewModel: ViewModel): Ready {
    const texts = getTexts(viewModel);
    return {
      type: 'ready',
      texts: texts,
      title: texts.title,
      navigation: {
        back: { title: '', image: 'ico_back_arrow', visible: true },
        home: { title: '', image: 'ico_home', visible: true },
        image: '',
      },
      body: texts.body,
      phone: texts.phone,
      close: { title: '', image: 'ico_close', visible: true },
      ikon: 'ico_support',
      metaTitle: texts.metaTitle,
    };
  }

  export function start(viewModel: ViewModel): (dispatch: Dispatch<AnyAction>) => void {
    return (dispatch) => {
      dispatch(setViewModel(loading(viewModel)));
    };
  }

  export async function load(
    viewModel: ViewModel,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    return (dispatch) => {
      dispatch(setViewModel(ready(viewModel)));
    };
  }

  export async function close(
    viewModel: ViewModel,
    logoUrl: string,
  ): Promise<(dispatch: Dispatch<AnyAction>) => void> {
    return (dispatch) => {
      dispatch(setViewModel(none(logoUrl)));
    };
  }
}
